import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Euro,
  TrendingUp,
  CheckCircle,
  Clock,
  Zap,
  Flame,
  User,
  Filter,
  AlertCircle,
  Download,
  Building2,
  Save,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Banknote,
  ArrowDownCircle,
  ArrowUpCircle,
  Calendar,
  Wifi
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Provvigioni = () => {
  const [stats, setStats] = useState(null);
  const [pratiche, setPratiche] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('mese');
  const [filtroStato, setFiltroStato] = useState('da_assegnare');
  const [filtroAgente, setFiltroAgente] = useState('');
  const [showGettoneModal, setShowGettoneModal] = useState(false);
  const [selectedPratica, setSelectedPratica] = useState(null);
  const [gettoneValue, setGettoneValue] = useState('');
  const [selectedForPayment, setSelectedForPayment] = useState([]);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payNote, setPayNote] = useState('');
  
  // Piano Commissioni state
  const [commissioni, setCommissioni] = useState([]);
  const [commissioniLoading, setCommissioniLoading] = useState(true);
  const [savingComm, setSavingComm] = useState(null);
  const [editCommValues, setEditCommValues] = useState({});
  const [initializingComm, setInitializingComm] = useState(false);

  // Dashboard Provvigionale state
  const [dashboard, setDashboard] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [dashMese, setDashMese] = useState('');
  const [dashAgente, setDashAgente] = useState('');
  const [dashDataDa, setDashDataDa] = useState('');
  const [dashDataA, setDashDataA] = useState('');
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [selectedForIncasso, setSelectedForIncasso] = useState([]);

  const { hasRole } = useAuth();
  const isAdmin = hasRole(['super_admin']);

  useEffect(() => {
    fetchStats();
    fetchUsers();
    fetchCommissioni();
  }, [periodo]);

  useEffect(() => {
    fetchPratiche();
  }, [filtroStato, filtroAgente]);

  const fetchCommissioni = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/commissioni`);
      setCommissioni(res.data);
      const vals = {};
      res.data.forEach(c => { vals[c.id] = { ...c }; });
      setEditCommValues(vals);
    } catch (e) {
      console.error('Error fetching commissioni:', e);
    } finally {
      setCommissioniLoading(false);
    }
  }, []);

  const handleInitCommissioni = async () => {
    setInitializingComm(true);
    try {
      const res = await axios.post(`${API_URL}/api/commissioni/init`);
      toast.success(res.data.message);
      fetchCommissioni();
    } catch (e) {
      toast.error('Errore');
    } finally {
      setInitializingComm(false);
    }
  };

  const handleSaveCommissione = async (id) => {
    setSavingComm(id);
    try {
      const vals = editCommValues[id];
      await axios.put(`${API_URL}/api/commissioni/${id}`, vals);
      toast.success(`Commissioni ${vals.compagnia} aggiornate`);
      fetchCommissioni();
    } catch (e) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSavingComm(null);
    }
  };

  const updateCommField = (id, field, value) => {
    setEditCommValues(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: parseFloat(value) || 0 }
    }));
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/provvigioni/stats`, {
        params: { periodo }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPratiche = async () => {
    try {
      const params = new URLSearchParams();
      if (filtroStato) params.append('stato_gettone', filtroStato);
      if (filtroAgente) params.append('sub_agente_id', filtroAgente);

      const response = await axios.get(`${API_URL}/api/provvigioni/pratiche?${params}`);
      setPratiche(response.data);
    } catch (error) {
      console.error('Error fetching pratiche:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users?ruolo=sub_agent`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleAssegnaGettone = async () => {
    if (!selectedPratica || !gettoneValue) return;
    
    try {
      await axios.put(
        `${API_URL}/api/provvigioni/pratiche/${selectedPratica.id}/gettone?gettone=${parseFloat(gettoneValue)}`
      );
      toast.success(`Gettone di €${parseFloat(gettoneValue).toFixed(2)} assegnato`);
      setShowGettoneModal(false);
      setSelectedPratica(null);
      setGettoneValue('');
      fetchPratiche();
      fetchStats();
    } catch (error) {
      toast.error('Errore nell\'assegnazione del gettone');
    }
  };

  const openGettoneModal = (pratica) => {
    setSelectedPratica(pratica);
    setGettoneValue(pratica.gettone?.toString() || pratica.commissione_prevista?.toString() || '');
    setShowGettoneModal(true);
  };

  const togglePaymentSelection = (id) => {
    setSelectedForPayment(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const payable = pratiche.filter(p => p.gettone && !p.commissione_pagata).map(p => p.id);
    if (selectedForPayment.length === payable.length) {
      setSelectedForPayment([]);
    } else {
      setSelectedForPayment(payable);
    }
  };

  const handleBatchPay = async () => {
    if (!selectedForPayment.length) return;
    try {
      await axios.post(`${API_URL}/api/provvigioni/paga-batch`, {
        contract_ids: selectedForPayment,
        note_pagamento: payNote
      });
      const nomi = pratiche
        .filter(p => selectedForPayment.includes(p.id))
        .map(p => p.cliente_nome)
        .join(', ');
      toast.success(`Pagamento registrato per ${selectedForPayment.length} pratiche`);
      setSelectedForPayment([]);
      setShowPayModal(false);
      setPayNote('');
      fetchPratiche();
      fetchStats();
    } catch (error) {
      toast.error('Errore nel pagamento batch');
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '€0,00';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  // Dashboard Provvigionale functions
  const fetchDashboard = useCallback(async () => {
    setDashLoading(true);
    try {
      const params = {};
      if (dashMese && dashMese !== 'all') params.mese = dashMese;
      if (dashAgente && dashAgente !== 'all') params.sub_agente_id = dashAgente;
      if (dashDataDa) params.data_da = dashDataDa;
      if (dashDataA) params.data_a = dashDataA;
      const res = await axios.get(`${API_URL}/api/provvigioni/dashboard`, { params });
      setDashboard(res.data);
    } catch (e) {
      console.error('Dashboard error:', e);
    } finally {
      setDashLoading(false);
    }
  }, [dashMese, dashAgente, dashDataDa, dashDataA]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleSegnaIncasso = async (contractId) => {
    try {
      await axios.put(`${API_URL}/api/provvigioni/pratiche/${contractId}/incasso?incassato=true`);
      toast.success('Incasso registrato');
      fetchDashboard();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const handleIncassoBatch = async () => {
    if (!selectedForIncasso.length) return;
    try {
      await axios.post(`${API_URL}/api/provvigioni/incasso-batch`, { contract_ids: selectedForIncasso });
      toast.success(`Incasso registrato per ${selectedForIncasso.length} pratiche`);
      setSelectedForIncasso([]);
      fetchDashboard();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const toggleIncassoSelection = (id) => {
    setSelectedForIncasso(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const getMonthOptions = () => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
      months.push({ value: val, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return months;
  };

  const tipoIcon = (tipo) => {
    if (tipo === 'luce') return <Zap className="w-3.5 h-3.5 text-amber-500" />;
    if (tipo === 'gas') return <Flame className="w-3.5 h-3.5 text-sky-500" />;
    if (tipo === 'internet') return <Wifi className="w-3.5 h-3.5 text-violet-500" />;
    return <Zap className="w-3.5 h-3.5 text-slate-400" />;
  };

  const isPastDue = (dataPrevista) => {
    if (!dataPrevista) return false;
    return new Date(dataPrevista) < new Date();
  };

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filtroAgente) params.append('sub_agente_id', filtroAgente);
      const response = await axios.get(`${API_URL}/api/export/provvigioni?${params}`);
      
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + response.data.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV scaricato');
    } catch (error) {
      toast.error('Errore nell\'export CSV');
    }
  };

  if (!hasRole(['super_admin', 'sub_agent'])) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900">Accesso Negato</h2>
          <p className="text-slate-500 mt-2">Non hai i permessi per visualizzare le provvigioni.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="provvigioni-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Provvigioni</h1>
          <p className="text-slate-500 mt-1">Gestione gettoni e commissioni sub-agenti</p>
        </div>
        <div className="flex items-center gap-3">
          {selectedForPayment.length > 0 && (
            <Button onClick={() => setShowPayModal(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="paga-batch-btn">
              <CheckCircle className="w-4 h-4 mr-2" />
              Paga Selezionati ({selectedForPayment.length})
            </Button>
          )}
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-provvigioni-btn">
            <Download className="w-4 h-4 mr-2" />
            Scarica CSV
          </Button>
          <Select value={periodo} onValueChange={setPeriodo}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mese">Ultimo Mese</SelectItem>
              <SelectItem value="anno">Anno Corrente</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="stat-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500">Gettoni Mese</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1 tabular-nums">
                  {formatCurrency(stats?.totale_gettoni_mese)}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-100">
                <Euro className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500">Gettoni Anno</p>
                <p className="text-2xl font-bold text-sky-600 mt-1 tabular-nums">
                  {formatCurrency(stats?.totale_gettoni_anno)}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-sky-100">
                <TrendingUp className="w-5 h-5 text-sky-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card border-l-4 border-l-violet-500">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500">Profitto Agenzia (Anno)</p>
                <p className="text-2xl font-bold text-violet-600 mt-1 tabular-nums">
                  {formatCurrency(stats?.totale_agenzia_anno)}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">Mese: {formatCurrency(stats?.totale_agenzia_mese)}</p>
              </div>
              <div className="p-2 rounded-lg bg-violet-100">
                <TrendingUp className="w-5 h-5 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500">Da Assegnare</p>
                <p className="text-2xl font-bold text-amber-600 mt-1 tabular-nums">
                  {stats?.pratiche_da_pagare || 0}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">pratiche</p>
              </div>
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500">Assegnate</p>
                <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">
                  {stats?.pratiche_pagate || 0}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">pratiche</p>
              </div>
              <div className="p-2 rounded-lg bg-slate-200">
                <CheckCircle className="w-5 h-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="dashboard">
        <div className="overflow-x-auto -mx-1 px-1">
          <TabsList className="w-max min-w-full">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="text-xs sm:text-sm">Dashboard Provvigionale</TabsTrigger>
            <TabsTrigger value="pratiche" data-testid="tab-pratiche" className="text-xs sm:text-sm">Pratiche da Gestire</TabsTrigger>
            <TabsTrigger value="agenti" className="text-xs sm:text-sm">Riepilogo Agenti</TabsTrigger>
            <TabsTrigger value="piano" className="text-xs sm:text-sm">Piano Provvigionale</TabsTrigger>
          </TabsList>
        </div>

        {/* DASHBOARD PROVVIGIONALE TAB */}
        <TabsContent value="dashboard" className="mt-6 space-y-4">
          {/* Filtri */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-3 items-end">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs text-slate-500">Sub-Agente</Label>
                  <Select value={dashAgente} onValueChange={setDashAgente}>
                    <SelectTrigger data-testid="dash-filter-agent">
                      <SelectValue placeholder="Tutti gli agenti" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti gli agenti</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-xs text-slate-500">Mese Inserimento</Label>
                  <Select value={dashMese} onValueChange={setDashMese}>
                    <SelectTrigger data-testid="dash-filter-month">
                      <SelectValue placeholder="Tutti i mesi" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti i mesi</SelectItem>
                      {getMonthOptions().map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-xs text-slate-500">Data Da</Label>
                  <Input type="date" value={dashDataDa} onChange={(e) => setDashDataDa(e.target.value)} data-testid="dash-filter-from" />
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-xs text-slate-500">Data A</Label>
                  <Input type="date" value={dashDataA} onChange={(e) => setDashDataA(e.target.value)} data-testid="dash-filter-to" />
                </div>
                <Button variant="outline" size="sm" onClick={() => { setDashMese(''); setDashAgente(''); setDashDataDa(''); setDashDataA(''); }} data-testid="dash-reset-filters">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reset
                </Button>
              </div>
            </CardContent>
          </Card>

          {dashLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
            </div>
          ) : dashboard && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="border-l-4 border-l-sky-500">
                  <CardContent className="p-4">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Comm. Agenzia Totale</p>
                    <p className="text-xl font-bold text-slate-900 mt-1" data-testid="dash-total-agency">{formatCurrency(dashboard.totali.comm_agenzia)}</p>
                    <div className="flex justify-between mt-2 text-xs">
                      <span className="text-emerald-600 flex items-center gap-0.5"><ArrowDownCircle className="w-3 h-3" /> {formatCurrency(dashboard.totali.incassato)}</span>
                      <span className="text-amber-600 flex items-center gap-0.5"><Clock className="w-3 h-3" /> {formatCurrency(dashboard.totali.da_incassare)}</span>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-amber-500">
                  <CardContent className="p-4">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Gettoni Sub-Agenti</p>
                    <p className="text-xl font-bold text-slate-900 mt-1" data-testid="dash-total-gettoni">{formatCurrency(dashboard.totali.gettoni_sub)}</p>
                    <div className="flex justify-between mt-2 text-xs">
                      <span className="text-emerald-600 flex items-center gap-0.5"><CheckCircle className="w-3 h-3" /> {formatCurrency(dashboard.totali.pagato_sub)}</span>
                      <span className="text-red-500 flex items-center gap-0.5"><AlertCircle className="w-3 h-3" /> {formatCurrency(dashboard.totali.da_pagare_sub)}</span>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-emerald-500">
                  <CardContent className="p-4">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Margine Netto</p>
                    <p className={`text-xl font-bold mt-1 ${dashboard.totali.margine >= 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="dash-margin">
                      {formatCurrency(dashboard.totali.margine)}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-2">Incassato - Pagato</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-fuchsia-500">
                  <CardContent className="p-4">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Margine Agenzia (Previsto)</p>
                    <p className={`text-xl font-bold mt-1 ${(dashboard.totali.margine_previsto || 0) >= 0 ? 'text-fuchsia-700' : 'text-red-600'}`} data-testid="dash-margin-previsto">
                      {formatCurrency(dashboard.totali.margine_previsto || 0)}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-2">Comm. agenzia - gettone sub</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-violet-500">
                  <CardContent className="p-4">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Contratti</p>
                    <p className="text-xl font-bold text-slate-900 mt-1" data-testid="dash-total-contracts">{dashboard.totali.totale_contratti}</p>
                    <p className="text-[10px] text-slate-400 mt-2">{dashboard.per_agente.length} sub-agenti attivi</p>
                  </CardContent>
                </Card>
              </div>

              {/* Per-Agent Breakdown */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2"><User className="w-4 h-4 text-slate-500" /> Riepilogo per Sub-Agente</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 border-b text-xs text-slate-500 uppercase tracking-wider">
                          <th className="px-4 py-2.5 text-left">Sub-Agente</th>
                          <th className="px-3 py-2.5 text-center">Contratti</th>
                          <th className="px-3 py-2.5 text-right">Comm. Agenzia</th>
                          <th className="px-3 py-2.5 text-right">Incassato</th>
                          <th className="px-3 py-2.5 text-right">Da Incassare</th>
                          <th className="px-3 py-2.5 text-right">Gettoni</th>
                          <th className="px-3 py-2.5 text-right">Pagato Sub</th>
                          <th className="px-3 py-2.5 text-right">Da Pagare Sub</th>
                          <th className="px-3 py-2.5 text-right">Margine</th>
                          <th className="px-3 py-2.5 text-right">Margine Agenzia (Prev.)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.per_agente.map((ag) => (
                          <React.Fragment key={ag.id}>
                            <tr 
                              className="border-b hover:bg-slate-50 cursor-pointer transition-colors"
                              onClick={() => setExpandedAgent(expandedAgent === ag.id ? null : ag.id)}
                              data-testid={`dash-agent-row-${ag.id}`}
                            >
                              <td className="px-4 py-3 font-medium text-slate-900 flex items-center gap-2">
                                {expandedAgent === ag.id ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                                {ag.nome}
                              </td>
                              <td className="px-3 py-3 text-center">
                                <Badge variant="outline" className="text-xs">{ag.totale_contratti}</Badge>
                              </td>
                              <td className="px-3 py-3 text-right font-medium">{formatCurrency(ag.comm_agenzia)}</td>
                              <td className="px-3 py-3 text-right text-emerald-600">{formatCurrency(ag.incassato)}</td>
                              <td className="px-3 py-3 text-right text-amber-600">{formatCurrency(ag.da_incassare)}</td>
                              <td className="px-3 py-3 text-right font-medium">{formatCurrency(ag.gettoni)}</td>
                              <td className="px-3 py-3 text-right text-emerald-600">{formatCurrency(ag.pagato_sub)}</td>
                              <td className="px-3 py-3 text-right text-red-500">{formatCurrency(ag.da_pagare_sub)}</td>
                              <td className={`px-3 py-3 text-right font-bold ${ag.margine >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{formatCurrency(ag.margine)}</td>
                              <td className={`px-3 py-3 text-right font-bold ${(ag.margine_previsto || 0) >= 0 ? 'text-fuchsia-700' : 'text-red-600'}`} data-testid={`agent-margine-previsto-${ag.id}`}>{formatCurrency(ag.margine_previsto || 0)}</td>
                            </tr>
                            {expandedAgent === ag.id && (
                              <tr>
                                <td colSpan="10" className="px-0 py-0">
                                  <div className="bg-slate-50/80 border-y">
                                    {/* Batch actions */}
                                    {selectedForIncasso.length > 0 && (
                                      <div className="px-4 py-2 bg-sky-50 border-b border-sky-100 flex items-center justify-between">
                                        <span className="text-xs text-sky-700">{selectedForIncasso.length} pratiche selezionate</span>
                                        <Button size="sm" className="bg-sky-600 hover:bg-sky-700 h-7 text-xs" onClick={handleIncassoBatch} data-testid="batch-incasso-btn">
                                          <ArrowDownCircle className="w-3 h-3 mr-1" /> Segna Incassate
                                        </Button>
                                      </div>
                                    )}
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="text-[10px] text-slate-400 uppercase">
                                          <th className="px-4 py-2 text-left w-8"></th>
                                          <th className="px-2 py-2 text-left">Cliente</th>
                                          <th className="px-2 py-2 text-left">Tipo</th>
                                          <th className="px-2 py-2 text-left">Fornitore</th>
                                          <th className="px-2 py-2 text-left">Inserito il</th>
                                          <th className="px-2 py-2 text-left">Previsto Pag.</th>
                                          <th className="px-2 py-2 text-right">Comm. Ag.</th>
                                          <th className="px-2 py-2 text-center">Incassato</th>
                                          <th className="px-2 py-2 text-right">Gettone</th>
                                          <th className="px-2 py-2 text-right">Margine Ag.</th>
                                          <th className="px-2 py-2 text-center">Pagato Sub</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(dashboard.pratiche || []).filter(p => p.sub_agente_id === ag.id).map(p => (
                                          <tr key={p.id} className="border-t border-slate-100 hover:bg-white transition-colors" data-testid={`dash-pratica-${p.id}`}>
                                            <td className="px-4 py-2">
                                              {!p.incassato_da_compagnia && (
                                                <input type="checkbox" className="rounded border-slate-300"
                                                  checked={selectedForIncasso.includes(p.id)}
                                                  onChange={() => toggleIncassoSelection(p.id)} />
                                              )}
                                            </td>
                                            <td className="px-2 py-2 font-medium text-slate-700">{p.cliente_nome}</td>
                                            <td className="px-2 py-2">{tipoIcon(p.tipologia)}</td>
                                            <td className="px-2 py-2 text-slate-600">{p.fornitore || '-'}</td>
                                            <td className="px-2 py-2 text-slate-500">{formatDate(p.created_at)}</td>
                                            <td className="px-2 py-2">
                                              <span className={`${isPastDue(p.data_prevista_pagamento) && !p.incassato_da_compagnia ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                                                {formatDate(p.data_prevista_pagamento)}
                                              </span>
                                              {isPastDue(p.data_prevista_pagamento) && !p.incassato_da_compagnia && (
                                                <span className="ml-1 text-[9px] text-red-500 font-bold">SCADUTO</span>
                                              )}
                                            </td>
                                            <td className="px-2 py-2 text-right font-medium">{formatCurrency(p.commissione_agenzia)}</td>
                                            <td className="px-2 py-2 text-center">
                                              {p.incassato_da_compagnia ? (
                                                <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" data-testid={`incassato-${p.id}`}>Incassato</Badge>
                                              ) : (
                                                <Button size="sm" variant="outline" className="h-6 text-[10px] border-amber-300 text-amber-700 hover:bg-amber-50"
                                                  onClick={(e) => { e.stopPropagation(); handleSegnaIncasso(p.id); }}
                                                  data-testid={`segna-incasso-${p.id}`}>
                                                  <ArrowDownCircle className="w-3 h-3 mr-0.5" /> Segna
                                                </Button>
                                              )}
                                            </td>
                                            <td className="px-2 py-2 text-right font-medium">{formatCurrency(p.gettone)}</td>
                                            <td className={`px-2 py-2 text-right font-bold ${(p.margine_agenzia || 0) >= 0 ? 'text-fuchsia-700' : 'text-red-600'}`} data-testid={`margine-ag-${p.id}`}>
                                              {formatCurrency(p.margine_agenzia || 0)}
                                            </td>
                                            <td className="px-2 py-2 text-center">
                                              {p.commissione_pagata ? (
                                                <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" data-testid={`pagato-sub-${p.id}`}>Pagato</Badge>
                                              ) : p.gettone > 0 ? (
                                                <Badge className="bg-red-50 text-red-600 text-[10px]">Da pagare</Badge>
                                              ) : (
                                                <span className="text-slate-300">-</span>
                                              )}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Per-Month Breakdown */}
              {dashboard.per_mese.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-500" /> Riepilogo per Mese</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b text-xs text-slate-500 uppercase tracking-wider">
                            <th className="px-4 py-2.5 text-left">Mese</th>
                            <th className="px-3 py-2.5 text-center">Contratti</th>
                            <th className="px-3 py-2.5 text-right">Comm. Agenzia</th>
                            <th className="px-3 py-2.5 text-right">Incassato</th>
                            <th className="px-3 py-2.5 text-right">Gettoni</th>
                            <th className="px-3 py-2.5 text-right">Pagato Sub</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboard.per_mese.map(m => {
                            const monthLabel = new Date(m.mese + '-01').toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
                            return (
                              <tr key={m.mese} className="border-b hover:bg-slate-50">
                                <td className="px-4 py-3 font-medium capitalize">{monthLabel}</td>
                                <td className="px-3 py-3 text-center"><Badge variant="outline">{m.contratti}</Badge></td>
                                <td className="px-3 py-3 text-right">{formatCurrency(m.comm_agenzia)}</td>
                                <td className="px-3 py-3 text-right text-emerald-600">{formatCurrency(m.incassato)}</td>
                                <td className="px-3 py-3 text-right">{formatCurrency(m.gettoni)}</td>
                                <td className="px-3 py-3 text-right text-emerald-600">{formatCurrency(m.pagato_sub)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Pratiche Tab */}
        <TabsContent value="pratiche" className="mt-6 space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <Select value={filtroStato || "all"} onValueChange={(v) => setFiltroStato(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Stato gettone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte</SelectItem>
                    <SelectItem value="da_assegnare">Da assegnare</SelectItem>
                    <SelectItem value="assegnato">Già assegnato</SelectItem>
                    <SelectItem value="da_pagare">Da pagare</SelectItem>
                    <SelectItem value="pagato">Pagato</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filtroAgente || "all"} onValueChange={(v) => setFiltroAgente(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-56">
                    <SelectValue placeholder="Sub-agente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli agenti</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Pratiche Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="table-header">
                      <th className="px-3 py-3 w-10">
                        <input type="checkbox" onChange={toggleSelectAll} 
                          checked={selectedForPayment.length > 0 && selectedForPayment.length === pratiche.filter(p => p.gettone && !p.commissione_pagata).length}
                          className="rounded" data-testid="select-all-checkbox" />
                      </th>
                      <th className="text-left px-4 py-3">Pratica</th>
                      <th className="text-left px-4 py-3">Cliente</th>
                      <th className="text-left px-4 py-3">Sub-agente</th>
                      <th className="text-left px-4 py-3">Stato</th>
                      <th className="text-right px-4 py-3">Gettone</th>
                      <th className="text-left px-4 py-3">Pagamento</th>
                      <th className="text-left px-4 py-3">Data</th>
                      <th className="text-right px-4 py-3">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pratiche.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-4 py-12 text-center text-slate-500">
                          Nessuna pratica trovata
                        </td>
                      </tr>
                    ) : (
                      pratiche.map((pratica) => (
                        <tr key={pratica.id} className={`table-row ${selectedForPayment.includes(pratica.id) ? 'bg-emerald-50' : ''}`} data-testid={`pratica-row-${pratica.id}`}>
                          <td className="px-3 py-3">
                            {pratica.gettone && !pratica.commissione_pagata ? (
                              <input type="checkbox" 
                                checked={selectedForPayment.includes(pratica.id)} 
                                onChange={() => togglePaymentSelection(pratica.id)}
                                className="rounded"
                              />
                            ) : pratica.commissione_pagata ? (
                              <CheckCircle className="w-4 h-4 text-emerald-500" />
                            ) : null}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {pratica.tipologia === 'luce' ? (
                                <Zap className="w-4 h-4 text-amber-500" />
                              ) : pratica.tipologia === 'gas' ? (
                                <Flame className="w-4 h-4 text-sky-500" />
                              ) : (
                                <div className="flex">
                                  <Zap className="w-3 h-3 text-amber-500" />
                                  <Flame className="w-3 h-3 text-sky-500 -ml-0.5" />
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-sm">{pratica.fornitore || 'N/D'}</p>
                                <p className="text-xs text-slate-500">{pratica.offerta || 'N/D'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-900">
                            {pratica.cliente_nome || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400" />
                              <span className="text-sm">{pratica.sub_agente_nome || '-'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                              pratica.stato === 'attiva' ? 'bg-green-100 text-green-700' : 'bg-emerald-100 text-emerald-700'
                            }`}>
                              {pratica.stato}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {pratica.gettone ? (
                              <span className="font-semibold text-emerald-600 tabular-nums">
                                {formatCurrency(pratica.gettone)}
                              </span>
                            ) : (
                              <span className="text-amber-600 font-medium text-sm">Da assegnare</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {pratica.commissione_pagata ? (
                              <div>
                                <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Pagato</span>
                                {pratica.note_pagamento && <p className="text-xs text-slate-500 mt-1">{pratica.note_pagamento}</p>}
                                {pratica.data_pagamento && <p className="text-xs text-slate-400">{formatDate(pratica.data_pagamento)}</p>}
                              </div>
                            ) : pratica.gettone ? (
                              <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Da pagare</span>
                            ) : (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-500 tabular-nums">
                            {formatDate(pratica.created_at)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              size="sm"
                              variant={pratica.gettone ? "outline" : "default"}
                              onClick={() => openGettoneModal(pratica)}
                              data-testid={`assegna-gettone-${pratica.id}`}
                            >
                              {pratica.gettone ? 'Modifica' : 'Assegna'}
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Agenti Tab */}
        <TabsContent value="agenti" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Riepilogo Gettoni per Sub-agente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="table-header">
                      <th className="text-left px-4 py-3">Sub-agente</th>
                      <th className="text-right px-4 py-3">Pratiche</th>
                      <th className="text-right px-4 py-3">Gettoni Mese</th>
                      <th className="text-right px-4 py-3">Totale Gettoni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats?.gettoni_per_agente?.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-12 text-center text-slate-500">
                          Nessun dato disponibile
                        </td>
                      </tr>
                    ) : (
                      stats?.gettoni_per_agente?.map((agente, index) => (
                        <tr key={agente.id} className="table-row">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                                index === 0 ? 'bg-amber-100 text-amber-700' :
                                index === 1 ? 'bg-slate-200 text-slate-700' :
                                index === 2 ? 'bg-orange-100 text-orange-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {index + 1}
                              </span>
                              <span className="font-medium text-slate-900">{agente.nome}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                            {agente.totale_pratiche}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-900">
                            {formatCurrency(agente.gettoni_mese)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-semibold text-emerald-600 tabular-nums">
                              {formatCurrency(agente.totale_gettoni)}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Piano Provvigionale Tab */}
        <TabsContent value="piano" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Piano Commissioni per Compagnia</h2>
              <p className="text-sm text-slate-500">Configura le commissioni per ruolo e tipologia</p>
            </div>
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={handleInitCommissioni} disabled={initializingComm} data-testid="init-commissioni-btn">
                <RefreshCw className={`w-4 h-4 mr-1.5 ${initializingComm ? 'animate-spin' : ''}`} />
                {initializingComm ? 'Sincronizzazione...' : 'Sincronizza Compagnie'}
              </Button>
            )}
          </div>

          {commissioniLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          ) : commissioni.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Euro className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500 text-lg">Nessun piano commissioni configurato</p>
                {isAdmin && (
                  <Button className="mt-4" onClick={handleInitCommissioni}>Inizializza dalle Compagnie</Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {commissioni.map((c) => (
                <Card key={c.id} data-testid={`commissione-${c.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <Building2 className="w-5 h-5 text-slate-400" />
                        <h3 className="text-lg font-bold text-slate-900">{c.compagnia}</h3>
                      </div>
                      {isAdmin && (
                        <Button
                          size="sm"
                          onClick={() => handleSaveCommissione(c.id)}
                          disabled={savingComm === c.id}
                          data-testid={`save-comm-${c.id}`}
                        >
                          {savingComm === c.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                          Salva
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Super Admin */}
                      {hasRole(['super_admin']) && (
                        <div className="border rounded-lg p-3 bg-purple-50/50">
                          <p className="text-xs font-semibold text-purple-700 mb-3 flex items-center gap-1">
                            <Badge className="bg-purple-100 text-purple-700 text-[10px]">Super Admin</Badge>
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {[
                              { field: 'super_admin_luce', icon: <Zap className="w-2.5 h-2.5" />, label: 'Luce' },
                              { field: 'super_admin_gas', icon: <Flame className="w-2.5 h-2.5" />, label: 'Gas' },
                              { field: 'super_admin_dual', icon: null, label: 'Dual' }
                            ].map(f => (
                              <div key={f.field} className="space-y-1">
                                <label className="text-[10px] text-slate-400 uppercase tracking-wider flex items-center gap-0.5">{f.icon}{f.label}</label>
                                <div className="relative">
                                  <Input type="number" step="0.01" min="0" value={editCommValues[c.id]?.[f.field] ?? 0} onChange={(e) => updateCommField(c.id, f.field, e.target.value)} disabled={!isAdmin} className="h-8 text-sm pr-6" data-testid={`comm-${c.id}-${f.field}`} />
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">EUR</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Agente */}
                      {hasRole(['super_admin', 'sub_agent']) && (
                        <div className="border rounded-lg p-3 bg-sky-50/50">
                          <p className="text-xs font-semibold text-sky-700 mb-3 flex items-center gap-1">
                            <Badge className="bg-sky-100 text-sky-700 text-[10px]">Agente</Badge>
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {[
                              { field: 'agente_luce', icon: <Zap className="w-2.5 h-2.5" />, label: 'Luce' },
                              { field: 'agente_gas', icon: <Flame className="w-2.5 h-2.5" />, label: 'Gas' },
                              { field: 'agente_dual', icon: null, label: 'Dual' }
                            ].map(f => (
                              <div key={f.field} className="space-y-1">
                                <label className="text-[10px] text-slate-400 uppercase tracking-wider flex items-center gap-0.5">{f.icon}{f.label}</label>
                                <div className="relative">
                                  <Input type="number" step="0.01" min="0" value={editCommValues[c.id]?.[f.field] ?? 0} onChange={(e) => updateCommField(c.id, f.field, e.target.value)} disabled={!isAdmin} className="h-8 text-sm pr-6" data-testid={`comm-${c.id}-${f.field}`} />
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">EUR</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Sub-agente */}
                      <div className="border rounded-lg p-3 bg-emerald-50/50">
                        <p className="text-xs font-semibold text-emerald-700 mb-3 flex items-center gap-1">
                          <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Sub-agente</Badge>
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {[
                            { field: 'sub_agente_luce', icon: <Zap className="w-2.5 h-2.5" />, label: 'Luce' },
                            { field: 'sub_agente_gas', icon: <Flame className="w-2.5 h-2.5" />, label: 'Gas' },
                            { field: 'sub_agente_dual', icon: null, label: 'Dual' }
                          ].map(f => (
                            <div key={f.field} className="space-y-1">
                              <label className="text-[10px] text-slate-400 uppercase tracking-wider flex items-center gap-0.5">{f.icon}{f.label}</label>
                              <div className="relative">
                                <Input type="number" step="0.01" min="0" value={editCommValues[c.id]?.[f.field] ?? 0} onChange={(e) => updateCommField(c.id, f.field, e.target.value)} disabled={!isAdmin} className="h-8 text-sm pr-6" data-testid={`comm-${c.id}-${f.field}`} />
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">EUR</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Assegna Gettone Modal */}
      <Dialog open={showGettoneModal} onOpenChange={setShowGettoneModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assegna Gettone</DialogTitle>
          </DialogHeader>
          {selectedPratica && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-50 rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  {selectedPratica.tipologia === 'luce' ? (
                    <Zap className="w-5 h-5 text-amber-500" />
                  ) : selectedPratica.tipologia === 'gas' ? (
                    <Flame className="w-5 h-5 text-sky-500" />
                  ) : (
                    <div className="flex">
                      <Zap className="w-4 h-4 text-amber-500" />
                      <Flame className="w-4 h-4 text-sky-500 -ml-1" />
                    </div>
                  )}
                  <span className="font-medium">{selectedPratica.fornitore} - {selectedPratica.offerta}</span>
                </div>
                <p className="text-sm text-slate-600">Cliente: {selectedPratica.cliente_nome}</p>
                <p className="text-sm text-slate-600">Sub-agente: {selectedPratica.sub_agente_nome}</p>
                {selectedPratica.commissione_prevista && (
                  <p className="text-sm text-slate-500">
                    Commissione prevista: {formatCurrency(selectedPratica.commissione_prevista)}
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="gettone">Importo Gettone (EUR)</Label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="gettone"
                    type="number"
                    step="0.01"
                    min="0"
                    value={gettoneValue}
                    onChange={(e) => setGettoneValue(e.target.value)}
                    className="pl-10"
                    placeholder="0.00"
                    data-testid="gettone-input"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGettoneModal(false)}>Annulla</Button>
            <Button onClick={handleAssegnaGettone} disabled={!gettoneValue || parseFloat(gettoneValue) < 0} data-testid="conferma-gettone-btn">
              Conferma Gettone
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pagamento Batch Modal */}
      <Dialog open={showPayModal} onOpenChange={setShowPayModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Registra Pagamento Commissioni</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="font-medium text-emerald-800">Stai pagando {selectedForPayment.length} pratiche:</p>
              <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {pratiche.filter(p => selectedForPayment.includes(p.id)).map(p => (
                  <div key={p.id} className="flex justify-between text-sm">
                    <span>{p.cliente_nome} - {p.sub_agente_nome}</span>
                    <span className="font-medium">{formatCurrency(p.gettone)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-2 border-t border-emerald-300 flex justify-between font-bold text-emerald-900">
                <span>Totale</span>
                <span>{formatCurrency(pratiche.filter(p => selectedForPayment.includes(p.id)).reduce((sum, p) => sum + (p.gettone || 0), 0))}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Note di pagamento (es. "Bonifico del 07/03/2026")</Label>
              <textarea 
                className="w-full h-20 px-3 py-2 text-sm border rounded-md" 
                value={payNote} 
                onChange={(e) => setPayNote(e.target.value)} 
                placeholder="Inserisci dettagli del bonifico..."
                data-testid="pay-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayModal(false)}>Annulla</Button>
            <Button onClick={handleBatchPay} className="bg-emerald-600 hover:bg-emerald-700" data-testid="conferma-pagamento-btn">
              <CheckCircle className="w-4 h-4 mr-2" />
              Conferma Pagamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Provvigioni;
